/**
 * Pagination
 *
 * Paginated lists with controls, counters, and dot navigation.
 * Supports infinite looping, mobile/desktop layouts, and full accessibility.
 *
 * Features:
 * - Infinite loop navigation with cloned pages
 * - Responsive items-per-page (desktop/mobile breakpoints)
 * - Dot navigation with keyboard support
 * - Live region announcements for screen readers
 * - Auto-hide controls for single page
 * - Dynamic height adjustment
 * - Focus management with inert attribute
 *
 * HTML Structure:
 *   <div data-hs-pagination="wrapper">
 *     <div data-hs-pagination="list">
 *       <!-- Items here -->
 *     </div>
 *     <div data-hs-pagination="controls" data-hs-pagination-show="6" data-hs-pagination-show-mobile="3">
 *       <div data-hs-pagination="previous"><button>Prev</button></div>
 *       <div data-hs-pagination="counter"></div>
 *       <div data-hs-pagination="next"><button>Next</button></div>
 *     </div>
 *     <div data-hs-pagination="dots">
 *       <div class="is-active">•</div>
 *       <div>•</div>
 *     </div>
 *   </div>
 */

import { querySelectorAll, querySelector, getSelector, globalConfig, cssVariables } from '@utils';

export function init(config) {
  const cleanup = {
    observers: [] as ResizeObserver[],
    handlers: [] as Array<{ element: Element; event: string; handler: EventListener }>,
    liveRegions: [] as HTMLElement[],
  };

  // Initialize all pagination containers
  try {
    querySelectorAll(config, 'wrapper').forEach((container) => {
      try {
        const instance = initPaginationInstance(container, cleanup, config);
        if (!instance) {
          console.warn('[pagination] Failed to initialize container', container);
        }
      } catch (error) {
        console.error('[pagination] Error initializing container:', error, container);
      }
    });
  } catch (error) {
    console.error('[pagination] Critical error during initialization:', error);
  }

  return {
    result: 'pagination initialized',
    destroy: () => {
      try {
        // Remove all live regions
        cleanup.liveRegions.forEach((liveRegion) => {
          try {
            if (liveRegion.parentNode) {
              liveRegion.parentNode.removeChild(liveRegion);
            }
          } catch (error) {
            console.error('[pagination] Error removing live region:', error);
          }
        });
        cleanup.liveRegions.length = 0;

        // Disconnect all observers
        cleanup.observers.forEach((obs) => {
          try {
            obs.disconnect();
          } catch (error) {
            console.error('[pagination] Error disconnecting observer:', error);
          }
        });
        cleanup.observers.length = 0;

        // Remove all event listeners
        cleanup.handlers.forEach(({ element, event, handler }) => {
          try {
            element.removeEventListener(event, handler);
          } catch (error) {
            console.error('[pagination] Error removing event listener:', error);
          }
        });
        cleanup.handlers.length = 0;
      } catch (error) {
        console.error('[pagination] Critical error during cleanup:', error);
      }
    },
  };
}

function initPaginationInstance(
  container: Element,
  cleanup: {
    observers: ResizeObserver[];
    handlers: Array<{ element: Element; event: string; handler: EventListener }>;
    liveRegions: HTMLElement[];
  },
  config: any
) {
  const list = querySelector(config, 'list', container) as HTMLElement;
  if (!list) {
    console.warn('[pagination] Missing required element: list');
    return null;
  }

  const wrapper = list.parentElement as HTMLElement;
  if (!wrapper) return null;

  const elements = {
    controls: querySelector(config, 'controls', container) as HTMLElement | null,
    counter: querySelector(config, 'counter', container) as HTMLElement | null,
    dotsWrap: querySelector(config, 'dots', container) as HTMLElement | null,
    nextButton: null as HTMLElement | null,
    prevButton: null as HTMLElement | null,
  };

  // Early exit for infinite mode - no controls means no pagination
  if (!elements.controls) {
    return { initialized: false };
  }

  // Find next/previous buttons using clickable pattern
  const nextClickable = querySelector(config, 'next', container);
  const prevClickable = querySelector(config, 'previous', container);

  const clickableSelector = getSelector(
    globalConfig.clickable as {
      attributes: { elements: { [key: string]: { primary: string; aliases?: string[] } } };
    },
    'button'
  );
  const nextButton = (nextClickable?.querySelector(clickableSelector) ||
    nextClickable) as HTMLElement | null;
  const prevButton = (prevClickable?.querySelector(clickableSelector) ||
    prevClickable) as HTMLElement | null;

  if (!nextButton || !prevButton) {
    console.warn('[pagination] Missing required navigation buttons');
    return null;
  }

  elements.nextButton = nextButton;
  elements.prevButton = prevButton;

  // Add ARIA attributes to buttons
  elements.nextButton.setAttribute('aria-label', 'Go to next page');
  elements.prevButton.setAttribute('aria-label', 'Go to previous page');
  if (elements.counter) {
    elements.counter.setAttribute('aria-live', 'polite');
    elements.counter.setAttribute('aria-label', 'Current page');
  }

  // Parse configuration from attributes
  const desktopItems =
    parseInt(elements.controls?.getAttribute(config.attributes.properties.show) || '6') || 6;
  const mobileItems =
    parseInt(
      elements.controls?.getAttribute(config.attributes.properties.showMobile) ||
        String(desktopItems)
    ) || desktopItems;

  const isMobileLayout = () => {
    const stateValue = getComputedStyle(list).getPropertyValue(cssVariables.state).trim();
    return stateValue === globalConfig.cssVars.state.values.active;
  };

  const allItems = Array.from(list.children) as HTMLElement[];
  const totalItems = allItems.length;
  if (!totalItems) return null;

  const state = {
    totalPages: 1,
    currentIndex: 1,
    currentPage: 1,
    isAnimating: false,
    itemsPerPage: desktopItems,
    dotTemplates: { active: null as Element | null, inactive: null as Element | null },
  };
  let wrapperChildren: Element[] = [];

  // Create live region for announcements
  const liveRegion = document.createElement('div');
  liveRegion.className = 'sr-only';
  liveRegion.setAttribute('aria-live', 'assertive');
  liveRegion.setAttribute('aria-atomic', 'true');
  liveRegion.style.cssText =
    'position: absolute; left: -10000px; width: 1px; height: 1px; overflow: hidden;';
  container.appendChild(liveRegion);
  cleanup.liveRegions.push(liveRegion);

  const updateCounter = () => {
    if (elements.counter) {
      elements.counter.textContent = `${state.currentPage} / ${state.totalPages}`;
    }
  };

  const announcePageChange = () => {
    liveRegion.textContent = `Page ${state.currentPage} of ${state.totalPages}`;
    setTimeout(() => (liveRegion.textContent = ''), 1000);
  };

  const manageFocus = () => {
    wrapperChildren.forEach((page, index) => {
      const htmlPage = page as HTMLElement;
      if (index === state.currentIndex) {
        htmlPage.removeAttribute('inert');
      } else {
        htmlPage.setAttribute('inert', '');
      }
    });
  };

  const updateHeight = () => {
    const targetPage = wrapperChildren[state.currentIndex] as HTMLElement;
    if (targetPage && targetPage.offsetHeight !== undefined) {
      wrapper.style.height = targetPage.offsetHeight + 'px';
    }
  };

  const initializeDots = () => {
    if (!elements.dotsWrap) return;

    // Find existing dots as templates
    const existingDots = Array.from(elements.dotsWrap.children);
    if (!existingDots.length) return;

    // Identify active and inactive templates
    const activeDot = existingDots.find((dot) => dot.classList.contains('is-active'));
    const inactiveDot = existingDots.find((dot) => !dot.classList.contains('is-active'));

    // Store templates (use same template for both if only one exists)
    state.dotTemplates.active = activeDot
      ? (activeDot.cloneNode(true) as Element)
      : (existingDots[0].cloneNode(true) as Element);
    state.dotTemplates.inactive = inactiveDot
      ? (inactiveDot.cloneNode(true) as Element)
      : (existingDots[0].cloneNode(true) as Element);

    // Clear existing dots
    elements.dotsWrap.innerHTML = '';

    // Add pagination accessibility attributes
    elements.dotsWrap.setAttribute('role', 'group');
    elements.dotsWrap.setAttribute('aria-label', 'Page navigation');

    // Create dots for each page
    for (let i = 1; i <= state.totalPages; i++) {
      const dot = (i === 1 ? state.dotTemplates.active : state.dotTemplates.inactive)!.cloneNode(
        true
      ) as HTMLElement;

      // Add accessibility attributes
      dot.setAttribute('role', 'button');
      dot.setAttribute('tabindex', '0');
      dot.setAttribute('aria-label', `Go to page ${i}`);
      dot.setAttribute('aria-current', i === 1 ? 'page' : 'false');
      dot.setAttribute('data-page', String(i));

      // Set initial active state
      if (i === 1) {
        dot.classList.add('is-active');
      } else {
        dot.classList.remove('is-active');
      }

      // Click handler
      const clickHandler = () => navigateToPage(i);
      dot.addEventListener('click', clickHandler);
      cleanup.handlers.push({ element: dot, event: 'click', handler: clickHandler });

      // Keyboard handler
      const keyHandler = (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          navigateToPage(i);
        }
      };
      dot.addEventListener('keydown', keyHandler as EventListener);
      cleanup.handlers.push({
        element: dot,
        event: 'keydown',
        handler: keyHandler as EventListener,
      });

      elements.dotsWrap.appendChild(dot);
    }
  };

  const updateActiveDot = () => {
    if (!elements.dotsWrap) return;

    const dots = Array.from(elements.dotsWrap.children);
    dots.forEach((dot, index) => {
      const dotPage = index + 1;
      if (dotPage === state.currentPage) {
        dot.classList.add('is-active');
        dot.setAttribute('aria-current', 'page');
      } else {
        dot.classList.remove('is-active');
        dot.setAttribute('aria-current', 'false');
      }
    });
  };

  const initializePagination = (forceItemsPerPage: number | null = null) => {
    const currentIsMobile = isMobileLayout();
    state.itemsPerPage = forceItemsPerPage || (currentIsMobile ? mobileItems : desktopItems);
    state.totalPages = Math.ceil(totalItems / state.itemsPerPage);

    // Remove old dot event handlers to prevent memory leaks
    if (elements.dotsWrap) {
      cleanup.handlers = cleanup.handlers.filter(({ element }) => {
        return !elements.dotsWrap!.contains(element);
      });
    }

    // Clean up previous page lists
    Array.from(wrapper.children).forEach((child) => {
      if (child !== list) wrapper.removeChild(child);
    });
    list.innerHTML = '';
    allItems.forEach((item) => list.appendChild(item));

    // Single page case
    if (state.totalPages <= 1) {
      if (elements.controls) {
        if (elements.controls.contains(document.activeElement) && document.activeElement) {
          (document.activeElement as HTMLElement).blur();
        }
        elements.controls.style.display = 'none';
        elements.controls.setAttribute('aria-hidden', 'true');
      }
      if (elements.dotsWrap) {
        elements.dotsWrap.style.display = 'none';
        elements.dotsWrap.setAttribute('aria-hidden', 'true');
      }
      Object.assign(state, { totalPages: 1, currentIndex: 1, currentPage: 1, isAnimating: false });
      wrapper.style.cssText = `transform: translateX(0%); height: ${list.offsetHeight}px;`;
      wrapperChildren = [list];
      return 1;
    }

    // Show controls and dots
    if (elements.controls) {
      elements.controls.style.display = '';
      elements.controls.removeAttribute('aria-hidden');
    }
    if (elements.dotsWrap) {
      elements.dotsWrap.style.display = '';
      elements.dotsWrap.removeAttribute('aria-hidden');
    }

    // Create page lists
    const pageLists = Array.from({ length: state.totalPages }, (_, page) => {
      const pageList = list.cloneNode(false) as HTMLElement;
      const startIndex = page * state.itemsPerPage;
      const endIndex = Math.min(startIndex + state.itemsPerPage, totalItems);
      allItems
        .slice(startIndex, endIndex)
        .forEach((item) => pageList.appendChild(item.cloneNode(true)));
      return pageList;
    });

    // Insert cloned pages for infinite loop
    wrapper.insertBefore(pageLists[pageLists.length - 1].cloneNode(true), list);
    pageLists.slice(1).forEach((page) => wrapper.appendChild(page));
    wrapper.appendChild(pageLists[0].cloneNode(true));

    // Populate original list with first page
    list.innerHTML = '';
    Array.from(pageLists[0].children).forEach((item) => list.appendChild(item as Node));

    Object.assign(state, { currentIndex: 1, currentPage: 1, isAnimating: false });
    wrapperChildren = Array.from(wrapper.children);

    // Set initial position without animation
    wrapper.style.transition = 'none';
    wrapper.style.transform = 'translateX(-100%)';
    // Force reflow to apply transform immediately
    wrapper.offsetHeight;
    // Re-enable transitions
    wrapper.style.transition = '';

    updateCounter();
    updateHeight();
    manageFocus();
    initializeDots();
    return state.totalPages;
  };

  let currentLayoutIsMobile = isMobileLayout();

  const checkLayoutChange = () => {
    // Prevent resize during animation
    if (state.isAnimating) return;

    const newIsMobile = isMobileLayout();
    if (newIsMobile !== currentLayoutIsMobile) {
      currentLayoutIsMobile = newIsMobile;
      initializePagination();
    } else {
      updateHeight();
    }
  };

  const resizeObserver = new ResizeObserver(checkLayoutChange);
  resizeObserver.observe(wrapper);
  cleanup.observers.push(resizeObserver);

  initializePagination();

  const navigateToPage = (targetPage: number) => {
    if (state.isAnimating || state.totalPages <= 1 || targetPage === state.currentPage) return;
    state.isAnimating = true;

    state.currentIndex = targetPage;
    state.currentPage = targetPage;

    updateCounter();
    announcePageChange();
    updateHeight();
    updateActiveDot();

    if (isMobileLayout() && elements.controls) {
      setTimeout(() => {
        const controlsBottom =
          elements.controls.getBoundingClientRect().bottom + window.pageYOffset;
        const clearance = 5 * 16; // 5rem in pixels
        const targetScrollPosition = controlsBottom - window.innerHeight + clearance;
        window.scrollTo({ top: targetScrollPosition, behavior: 'smooth' });
      }, 50);
    }

    wrapper.style.transform = `translateX(${-state.currentIndex * 100}%)`;

    let transitionTimeout = null;

    const handleTransitionEnd = () => {
      clearTimeout(transitionTimeout);
      wrapper.removeEventListener('transitionend', handleTransitionEnd);

      updateCounter();
      announcePageChange();
      updateHeight();
      manageFocus();
      updateActiveDot();
      state.isAnimating = false;
    };

    // Safety timeout in case transitionend never fires
    transitionTimeout = setTimeout(handleTransitionEnd, 1000);

    wrapper.addEventListener('transitionend', handleTransitionEnd);
  };

  const navigate = (direction: number) => {
    if (state.isAnimating || state.totalPages <= 1) return;
    state.isAnimating = true;
    state.currentIndex += direction;

    state.currentPage =
      state.currentIndex > state.totalPages
        ? 1
        : state.currentIndex < 1
          ? state.totalPages
          : state.currentIndex;

    updateCounter();
    announcePageChange();
    updateHeight();
    updateActiveDot();

    if (isMobileLayout() && elements.controls) {
      setTimeout(() => {
        const controlsBottom =
          elements.controls.getBoundingClientRect().bottom + window.pageYOffset;
        const clearance = 5 * 16; // 5rem in pixels
        const targetScrollPosition = controlsBottom - window.innerHeight + clearance;
        window.scrollTo({ top: targetScrollPosition, behavior: 'smooth' });
      }, 50);
    }

    wrapper.style.transform = `translateX(${-state.currentIndex * 100}%)`;

    let transitionTimeout = null;

    const handleTransitionEnd = () => {
      clearTimeout(transitionTimeout);
      wrapper.removeEventListener('transitionend', handleTransitionEnd);

      if (state.currentIndex > state.totalPages) {
        state.currentIndex = 1;
        state.currentPage = 1;
        // Disable transition for instant jump
        wrapper.style.transition = 'none';
        wrapper.style.transform = 'translateX(-100%)';
        // Force reflow to apply the instant jump
        wrapper.offsetHeight;
        // Re-enable CSS transition
        wrapper.style.transition = '';
      } else if (state.currentIndex < 1) {
        state.currentIndex = state.totalPages;
        state.currentPage = state.totalPages;
        // Disable transition for instant jump
        wrapper.style.transition = 'none';
        wrapper.style.transform = `translateX(${-state.totalPages * 100}%)`;
        // Force reflow to apply the instant jump
        wrapper.offsetHeight;
        // Re-enable CSS transition
        wrapper.style.transition = '';
      }

      updateCounter();
      announcePageChange();
      updateHeight();
      manageFocus();
      updateActiveDot();
      state.isAnimating = false;
    };

    // Safety timeout in case transitionend never fires
    transitionTimeout = setTimeout(handleTransitionEnd, 1000);

    wrapper.addEventListener('transitionend', handleTransitionEnd);
  };

  const nextHandler = () => navigate(1);
  const prevHandler = () => navigate(-1);

  elements.nextButton.addEventListener('click', nextHandler);
  elements.prevButton.addEventListener('click', prevHandler);

  cleanup.handlers.push(
    { element: elements.nextButton, event: 'click', handler: nextHandler },
    { element: elements.prevButton, event: 'click', handler: prevHandler }
  );

  return { initialized: true };
}
